/* eslint-disable no-undef */
import React, { useState } from 'react';
import './Docs.css';

const GhostLogo = () => (
  <img src="ghost.png" alt="Ghost Protocol" width="40" height="40" style={{ borderRadius: '8px' }} />
);

const sections = [
  { id: 'overview', title: 'Overview', icon: '👻' },
  { id: 'how-it-works', title: 'How It Works', icon: '⚙️' },
  { id: 'getting-started', title: 'Getting Started', icon: '🚀' },
  { id: 'deposit', title: 'Making a Deposit', icon: '📥' },
  { id: 'withdraw', title: 'Withdrawing Funds', icon: '📤' },
  { id: 'note-safety', title: 'Note Safety', icon: '🔐' },
  { id: 'pools', title: 'Pool Denominations', icon: '💰' },
  { id: 'fees', title: 'Fees', icon: '💸' },
  { id: 'compliance', title: 'Compliance Tool', icon: '📋' },
  { id: 'nft', title: 'Ghost PFP NFTs', icon: '🖼️' },
  { id: 'governance', title: 'Governance', icon: '🗳️' },
  { id: 'staking', title: 'Staking $GHOST', icon: '🔒' },
  { id: 'proposals', title: 'Proposals & Voting', icon: '📝' },
  { id: 'faq', title: 'FAQ', icon: '❓' },
  { id: 'legal', title: 'Legal Disclaimer', icon: '⚖️' },
];

function Docs({ onBack }) {
  const [activeSection, setActiveSection] = useState('overview');

  const scrollToSection = (id) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="docs-container">
      {/* Header */}
      <header className="docs-header">
        <div className="docs-header-left">
          <div className="logo" onClick={onBack} style={{ cursor: 'pointer' }}>
            <GhostLogo />
            <span className="logo-text">Ghost Protocol</span>
          </div>
        </div>
        <div className="docs-header-right">
          <button className="back-btn" onClick={onBack}>
            ← Back to App
          </button>
        </div>
      </header>

      <div className="docs-layout">
        {/* Sidebar */}
        <aside className="docs-sidebar">
          <nav className="docs-nav">
            <h3>Documentation</h3>
            {sections.map((section) => (
              <button
                key={section.id}
                className={`docs-nav-item ${activeSection === section.id ? 'active' : ''}`}
                onClick={() => scrollToSection(section.id)}
              >
                <span className="nav-icon">{section.icon}</span>
                {section.title}
              </button>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="docs-content">
          {/* Overview */}
          <section id="overview" className="docs-section">
            <h1>👻 Ghost Protocol</h1>
            <p className="intro">
              Ghost Protocol is a privacy-preserving protocol built on Monad that allows users to break the on-chain link between sender and recipient addresses. Using zero-knowledge proofs, you can deposit funds and later withdraw them to any address without anyone being able to trace the connection.
            </p>
            
            <div className="highlight-box">
              <h4>🔒 Privacy Through Cryptography</h4>
              <p>
                Ghost Protocol uses the same battle-tested cryptographic techniques as other privacy protocols: Groth16 zero-knowledge proofs, Poseidon hashing, and Merkle trees. Your transactions are mathematically private, not just obscured.
              </p>
            </div>

            <h3>Key Features</h3>
            <ul className="feature-list">
              <li><strong>Complete Privacy:</strong> No on-chain link between deposits and withdrawals</li>
              <li><strong>Non-Custodial:</strong> Only you control your funds with your private note</li>
              <li><strong>Trustless:</strong> Smart contracts handle everything - no intermediaries</li>
              <li><strong>Fixed Denominations:</strong> Deposits of the same size create an anonymity set</li>
              <li><strong>Compliance Ready:</strong> Optional compliance tool for regulatory requirements</li>
              <li><strong>Community Governed:</strong> $GHOST token holders control the protocol's future</li>
            </ul>
          </section>

          {/* How It Works */}
          <section id="how-it-works" className="docs-section">
            <h2>⚙️ How It Works</h2>
            
            <div className="step-diagram">
              <div className="step">
                <div className="step-number">1</div>
                <h4>Deposit</h4>
                <p>You deposit a fixed amount of MON along with a cryptographic commitment (a hash of your secret note).</p>
              </div>
              <div className="step-arrow">→</div>
              <div className="step">
                <div className="step-number">2</div>
                <h4>Wait</h4>
                <p>Your deposit joins others of the same denomination, creating an "anonymity set."</p>
              </div>
              <div className="step-arrow">→</div>
              <div className="step">
                <div className="step-number">3</div>
                <h4>Withdraw</h4>
                <p>Using your secret note, generate a ZK proof that you made a deposit without revealing which one.</p>
              </div>
            </div>

            <h3>Zero-Knowledge Proofs</h3>
            <p>
              A zero-knowledge proof allows you to prove something is true without revealing the underlying information. In Ghost Protocol, you prove:
            </p>
            <ul>
              <li>"I know a secret note corresponding to one of the deposits in the pool"</li>
              <li>"I haven't withdrawn this deposit before"</li>
            </ul>
            <p>
              The contract verifies this proof without learning which deposit is yours, preserving your privacy.
            </p>

            <h3>The Anonymity Set</h3>
            <p>
              Your privacy is proportional to the number of deposits in your pool. If 100 people have deposited 500 MON, an observer only knows your withdrawal came from one of those 100 deposits - they can't determine which one.
            </p>
            <div className="info-box">
              <strong>💡 Tip:</strong> Wait for more deposits before withdrawing to increase your anonymity set.
            </div>
          </section>

          {/* Getting Started */}
          <section id="getting-started" className="docs-section">
            <h2>🚀 Getting Started</h2>
            
            <h3>Requirements</h3>
            <ul>
              <li><strong>MetaMask</strong> or compatible Web3 wallet</li>
              <li><strong>MON tokens</strong> on Monad mainnet</li>
              <li><strong>A secure place</strong> to store your private note</li>
            </ul>

            <h3>Connect Your Wallet</h3>
            <ol className="numbered-list">
              <li>Click the <strong>"Connect"</strong> button in the top right</li>
              <li>Approve the connection in MetaMask</li>
              <li>If prompted, allow the app to switch to Monad network</li>
              <li>You're ready to use Ghost Protocol!</li>
            </ol>

            <div className="warning-box">
              <strong>⚠️ Important:</strong> Make sure you're on the official Ghost Protocol website. Bookmark it to avoid phishing sites.
            </div>
          </section>

          {/* Making a Deposit */}
          <section id="deposit" className="docs-section">
            <h2>📥 Making a Deposit</h2>
            
            <ol className="numbered-list">
              <li>
                <strong>Select Amount:</strong> Choose your deposit amount from the available denominations (500, 5K, 50K, 500K, or 5M MON)
              </li>
              <li>
                <strong>Click Deposit:</strong> The app will generate your private note automatically
              </li>
              <li>
                <strong>Save Your Note:</strong> The note will be:
                <ul>
                  <li>Copied to your clipboard</li>
                  <li>Displayed on screen</li>
                  <li>Use the Download button to save a backup file. Store it somewhere safe!</li>
                </ul>
              </li>
              <li>
                <strong>Confirm Transaction:</strong> Approve the transaction in MetaMask
              </li>
              <li>
                <strong>Wait for Confirmation:</strong> Your deposit is complete when the transaction confirms
              </li>
            </ol>

            <div className="danger-box">
              <strong>🚨 CRITICAL:</strong> Your note is generated BEFORE the transaction. Save it immediately! Without this note, your funds cannot be recovered. Ever. By anyone.
            </div>
          </section>

          {/* Withdrawing Funds */}
          <section id="withdraw" className="docs-section">
            <h2>📤 Withdrawing Funds</h2>
            
            <ol className="numbered-list">
              <li>
                <strong>Go to Withdraw Tab:</strong> Click "Withdraw" at the top of the app
              </li>
              <li>
                <strong>Paste Your Note:</strong> Enter the private note you saved during deposit
              </li>
              <li>
                <strong>Enter Recipient:</strong> Any Monad address (doesn't have to be your connected wallet!)
              </li>
              <li>
                <strong>Click Withdraw:</strong> The app will:
                <ul>
                  <li>Verify your note is valid</li>
                  <li>Build the Merkle tree from all deposits</li>
                  <li>Generate a zero-knowledge proof (takes ~30-60 seconds)</li>
                </ul>
              </li>
              <li>
                <strong>Confirm Transaction:</strong> Approve in MetaMask
              </li>
              <li>
                <strong>Receive Funds:</strong> Minus the 1% protocol fee
              </li>
            </ol>

            <div className="info-box">
              <strong>💡 Privacy Tip:</strong> Withdraw to a fresh address that has never interacted with your deposit address for maximum privacy.
            </div>
          </section>

          {/* Note Safety */}
          <section id="note-safety" className="docs-section">
            <h2>🔐 Note Safety</h2>
            
            <p>Your private note is the ONLY way to withdraw your funds. Treat it like a private key.</p>

            <h3>Note Format</h3>
            <div className="code-block">
              ghost-monad-500-0x1a2b3c...def-0x9f8e7d...abc
            </div>
            <p>
              The note contains your denomination and two secret values (nullifier and secret) that prove ownership.
            </p>

            <h3>Storage Recommendations</h3>
            <div className="do-dont-grid">
              <div className="do-box">
                <h4>✅ DO</h4>
                <ul>
                  <li>Save multiple backups in different locations</li>
                  <li>Use encrypted storage (password manager, encrypted drive)</li>
                  <li>Write it down and store in a safe</li>
                  <li>Verify the backup is readable before depositing</li>
                </ul>
              </div>
              <div className="dont-box">
                <h4>❌ DON'T</h4>
                <ul>
                  <li>Store only in clipboard (it gets overwritten)</li>
                  <li>Email it to yourself unencrypted</li>
                  <li>Save in cloud storage without encryption</li>
                  <li>Share with anyone</li>
                  <li>Screenshot on your phone</li>
                </ul>
              </div>
            </div>

            <div className="danger-box">
              <strong>🚨 Lost Note = Lost Funds:</strong> There is no recovery mechanism. The developers cannot help you. This is by design - it's what makes the protocol trustless and private.
            </div>
          </section>

          {/* Pool Denominations */}
          <section id="pools" className="docs-section">
            <h2>💰 Pool Denominations</h2>
            
            <p>Ghost Protocol uses fixed deposit amounts to ensure all deposits in a pool are indistinguishable:</p>

            <table className="pools-table">
              <thead>
                <tr>
                  <th>Pool</th>
                  <th>Deposit Amount</th>
                  <th>Use Case</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Pool 1</td>
                  <td>500 MON</td>
                  <td>Small transactions, testing</td>
                </tr>
                <tr>
                  <td>Pool 2</td>
                  <td>5,000 MON</td>
                  <td>Medium transactions</td>
                </tr>
                <tr>
                  <td>Pool 3</td>
                  <td>50,000 MON</td>
                  <td>Larger transactions</td>
                </tr>
                <tr>
                  <td>Pool 4</td>
                  <td>500,000 MON</td>
                  <td>High-value privacy</td>
                </tr>
                <tr>
                  <td>Pool 5</td>
                  <td>5,000,000 MON</td>
                  <td>Whale transactions</td>
                </tr>
              </tbody>
            </table>

            <div className="info-box">
              <strong>💡 Why Fixed Amounts?</strong> If everyone deposited different amounts, observers could link deposits to withdrawals by matching values. Fixed denominations make all deposits in a pool identical.
            </div>
          </section>

          {/* Fees */}
          <section id="fees" className="docs-section">
            <h2>💸 Fees</h2>
            
            <table className="fees-table">
              <thead>
                <tr>
                  <th>Fee Type</th>
                  <th>Amount</th>
                  <th>When Charged</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Protocol Fee</td>
                  <td>1%</td>
                  <td>Deducted from withdrawal</td>
                </tr>
                <tr>
                  <td>Gas (Deposit)</td>
                  <td>~0.05 MON</td>
                  <td>Paid during deposit transaction</td>
                </tr>
                <tr>
                  <td>Gas (Withdraw)</td>
                  <td>~0.2 MON</td>
                  <td>Paid during withdrawal (ZK verification is expensive)</td>
                </tr>
              </tbody>
            </table>

            <h3>Example</h3>
            <div className="example-box">
              <p><strong>Deposit:</strong> 500 MON + ~0.05 MON gas</p>
              <p><strong>Withdraw:</strong> 495 MON received (500 - 1% fee) + ~0.2 MON gas</p>
              <p><strong>Total Cost:</strong> ~5.25 MON (1% + gas fees)</p>
            </div>
          </section>

          {/* Compliance */}
          <section id="compliance" className="docs-section">
            <h2>📋 Compliance Tool</h2>
            
            <p>
              Ghost Protocol includes an optional compliance feature for users who need to demonstrate the source of their funds for regulatory, tax, or legal purposes.
            </p>

            <h3>How It Works</h3>
            <ol className="numbered-list">
              <li>Click "Compliance" in the navigation</li>
              <li>Paste your private note</li>
              <li>Click "Generate Report"</li>
              <li>A compliance report is created on-chain linking your deposit to your identity (your connected wallet)</li>
            </ol>

            <div className="warning-box">
              <strong>⚠️ Privacy Tradeoff:</strong> Using the compliance tool creates a public record linking your wallet to a specific deposit. Only use this if legally required.
            </div>

            <h3>Use Cases</h3>
            <ul>
              <li>Tax reporting requirements</li>
              <li>Responding to legal inquiries</li>
              <li>Business compliance requirements</li>
              <li>Proving source of funds to exchanges</li>
            </ul>
          </section>

          {/* NFT Section */}
          <section id="nft" className="docs-section">
            <h2>🖼️ Ghost PFP NFTs</h2>
            
            <p className="intro">
              The Ghost PFP Collection is a limited series of 1,000 unique on-chain generative ghost NFTs. Each NFT is minted by burning $GHOST tokens, creating deflationary pressure on the token supply.
            </p>

            <div className="highlight-box">
              <h4>🔥 Burn-to-Mint Mechanism</h4>
              <p>
                Unlike traditional NFT mints that take payment, Ghost PFPs require you to <strong>permanently burn 100,000 $GHOST tokens</strong> per mint. These tokens are sent to the dead address and can never be recovered, reducing the total supply forever.
              </p>
            </div>

            <h3>Collection Details</h3>
            <table className="pools-table">
              <thead>
                <tr>
                  <th>Property</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Total Supply</td>
                  <td>1,000 NFTs (max)</td>
                </tr>
                <tr>
                  <td>Mint Price</td>
                  <td>100,000 GHOST (burned)</td>
                </tr>
                <tr>
                  <td>Token Standard</td>
                  <td>ERC-721</td>
                </tr>
                <tr>
                  <td>Metadata</td>
                  <td>100% On-Chain SVG</td>
                </tr>
                <tr>
                  <td>Burn Address</td>
                  <td>0x000...dEaD</td>
                </tr>
                <tr>
                  <td>Contract</td>
                  <td>0xEB036Af...BF6F</td>
                </tr>
              </tbody>
            </table>

            <h3>Randomized Traits</h3>
            <p>Each Ghost PFP is uniquely generated on-chain with randomized traits determined at mint time:</p>
            
            <table className="pools-table">
              <thead>
                <tr>
                  <th>Trait</th>
                  <th>Variations</th>
                  <th>Examples</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>🎨 Background</td>
                  <td>6 colors</td>
                  <td>Purple, Black, Green, Orange, Pink, Blue</td>
                </tr>
                <tr>
                  <td>👻 Ghost Color</td>
                  <td>6 variants</td>
                  <td>White, Gray, Blue, Purple, Gold, Rainbow</td>
                </tr>
                <tr>
                  <td>👀 Eyes</td>
                  <td>6 styles</td>
                  <td>Normal, Happy, Angry, Sleepy, Laser, Heart</td>
                </tr>
                <tr>
                  <td>👄 Mouth</td>
                  <td>5 types</td>
                  <td>Smile, Tongue, Open, Frown, Fangs</td>
                </tr>
                <tr>
                  <td>✨ Accessories</td>
                  <td>6 items</td>
                  <td>None, Bow, Crown, Halo, Horns, Top Hat</td>
                </tr>
                <tr>
                  <td>🌟 Effects</td>
                  <td>5 types</td>
                  <td>Shadow, Glow, Sparkles, Fire, Ice</td>
                </tr>
              </tbody>
            </table>

            <h3>Rarity Tiers</h3>
            <p>Each Ghost has a rarity tier that determines which traits it can have:</p>
            
            <table className="pools-table">
              <thead>
                <tr>
                  <th>Rarity</th>
                  <th>Chance</th>
                  <th>Special Traits</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>⚪ Common</td>
                  <td>50%</td>
                  <td>White/Gray ghost, basic eyes, no accessories</td>
                </tr>
                <tr>
                  <td>🟢 Uncommon</td>
                  <td>30%</td>
                  <td>Blue/Purple ghost, more expressions, Bow accessory</td>
                </tr>
                <tr>
                  <td>🔵 Rare</td>
                  <td>15%</td>
                  <td>Gold ghost, Crown/Halo/Horns, Sparkles/Fire effects</td>
                </tr>
                <tr>
                  <td>🟣 Legendary</td>
                  <td>5%</td>
                  <td>Rainbow ghost, Laser/Heart eyes, Top Hat, Fire/Ice effects</td>
                </tr>
              </tbody>
            </table>

            <h3>How to Mint</h3>
            <ol className="numbered-list">
              <li>
                <strong>Get $GHOST Tokens:</strong> You need at least 100,000 $GHOST in your wallet. Buy on <a href="https://nad.fun/tokens/0x601CE35D2aD556599F796323c98b25a8d5D17777" target="_blank" rel="noopener noreferrer">nad.fun</a>
              </li>
              <li>
                <strong>Go to NFT Page:</strong> Click "NFT" in the navigation menu
              </li>
              <li>
                <strong>Connect Wallet:</strong> Make sure your wallet is connected to Monad
              </li>
              <li>
                <strong>Approve GHOST:</strong> First transaction - approve the NFT contract to spend your GHOST tokens
              </li>
              <li>
                <strong>Mint:</strong> Second transaction - burn 100,000 GHOST and receive your unique Ghost PFP
              </li>
              <li>
                <strong>View Your NFT:</strong> Your Ghost will appear in your wallet and on NFT marketplaces
              </li>
            </ol>

            <div className="info-box">
              <strong>💡 Tip:</strong> The approval step only needs to be done once. After approving, you can mint multiple NFTs with just one transaction each.
            </div>

            <h3>Why Burn-to-Mint?</h3>
            <div className="do-dont-grid">
              <div className="do-box">
                <h4>🔥 Benefits</h4>
                <ul>
                  <li>Reduces $GHOST circulating supply permanently</li>
                  <li>Creates deflationary tokenomics</li>
                  <li>Aligns NFT holders with token holders</li>
                  <li>No team profit from minting - tokens are destroyed</li>
                  <li>Proves commitment to the ecosystem</li>
                </ul>
              </div>
              <div className="dont-box">
                <h4>⚠️ Consider</h4>
                <ul>
                  <li>Burned tokens cannot be recovered</li>
                  <li>100,000 GHOST is permanently destroyed per mint</li>
                  <li>NFT value is separate from token value</li>
                  <li>Limited to 1,000 total - once sold out, no more mints</li>
                </ul>
              </div>
            </div>

            <h3>On-Chain Artwork</h3>
            <p>
              Every Ghost PFP is generated and stored entirely on-chain as SVG code. This means:
            </p>
            <ul className="feature-list">
              <li><strong>Permanent:</strong> Your NFT exists as long as Monad exists - no IPFS, no external servers</li>
              <li><strong>Immutable:</strong> The artwork cannot be changed or taken down</li>
              <li><strong>Verifiable:</strong> Anyone can verify the traits directly from the smart contract</li>
              <li><strong>Self-Contained:</strong> The tokenURI returns the complete image data</li>
            </ul>

            <div className="warning-box">
              <strong>⚠️ No Refunds:</strong> Once you mint, the GHOST tokens are burned forever. Make sure you want the NFT before confirming the transaction. Each mint is final and irreversible.
            </div>

            <h3>Contract Addresses</h3>
            <div className="code-block">
NFT Contract: 0xEB036Af9fC62DCda581dF142C6bf7433ace7BF6F
GHOST Token:  0x601CE35D2aD556599F796323c98b25a8d5D17777
Burn Address: 0x000000000000000000000000000000000000dEaD
            </div>
          </section>

          {/* Governance */}
          <section id="governance" className="docs-section">
            <h2>🗳️ Governance</h2>
            
            <p>
              Ghost Protocol is governed by $GHOST token holders. The governance system allows the community to propose and vote on changes to the protocol, ensuring decentralized decision-making.
            </p>

            <div className="highlight-box">
              <h4>🏛️ Decentralized Control</h4>
              <p>
                No single entity controls Ghost Protocol. Token holders collectively make decisions about the protocol's future through on-chain voting.
              </p>
            </div>

            <h3>$GHOST Token</h3>
            <p>
              $GHOST is the governance token of Ghost Protocol. It serves two main purposes:
            </p>
            <ul className="feature-list">
              <li><strong>Voting Power:</strong> Stake $GHOST to vote on governance proposals</li>
              <li><strong>Staking Rewards:</strong> Earn rewards by staking and locking your tokens</li>
            </ul>

            <h3>Governance Parameters</h3>
            <table className="pools-table">
              <thead>
                <tr>
                  <th>Parameter</th>
                  <th>Value</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Proposal Threshold</td>
                  <td>1,000,000 GHOST</td>
                  <td>Minimum staked to create a proposal</td>
                </tr>
                <tr>
                  <td>Quorum</td>
                  <td>10,000,000 GHOST</td>
                  <td>Minimum votes for proposal to pass</td>
                </tr>
                <tr>
                  <td>Voting Period</td>
                  <td>3 days</td>
                  <td>Time to vote on proposals</td>
                </tr>
                <tr>
                  <td>Execution Delay</td>
                  <td>2 days</td>
                  <td>Timelock before execution</td>
                </tr>
                <tr>
                  <td>Execution Window</td>
                  <td>3 days</td>
                  <td>Time to execute after delay</td>
                </tr>
              </tbody>
            </table>
          </section>

          {/* Staking */}
          <section id="staking" className="docs-section">
            <h2>🔒 Staking $GHOST</h2>
            
            <p>
              Stake your $GHOST tokens to participate in governance and earn staking rewards. The longer you lock your tokens, the higher your reward multiplier.
            </p>

            <h3>Lock Tiers</h3>
            <table className="pools-table">
              <thead>
                <tr>
                  <th>Tier</th>
                  <th>Lock Duration</th>
                  <th>Reward Multiplier</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Tier 0</td>
                  <td>No Lock</td>
                  <td>1x (baseline)</td>
                </tr>
                <tr>
                  <td>Tier 1</td>
                  <td>30 Days</td>
                  <td>1.5x (+50%)</td>
                </tr>
                <tr>
                  <td>Tier 2</td>
                  <td>90 Days</td>
                  <td>2x (+100%)</td>
                </tr>
                <tr>
                  <td>Tier 3</td>
                  <td>180 Days</td>
                  <td>3x (+200%)</td>
                </tr>
                <tr>
                  <td>Tier 4</td>
                  <td>365 Days</td>
                  <td>5x (+400%)</td>
                </tr>
              </tbody>
            </table>

            <h3>How to Stake</h3>
            <ol className="numbered-list">
              <li>
                <strong>Go to Voting Page:</strong> Click "Voting" in the navigation
              </li>
              <li>
                <strong>Connect Wallet:</strong> Make sure your wallet is connected
              </li>
              <li>
                <strong>Click "Stake":</strong> Opens the staking modal
              </li>
              <li>
                <strong>Enter Amount:</strong> How much $GHOST to stake
              </li>
              <li>
                <strong>Select Lock Tier:</strong> Choose your lock duration for higher rewards
              </li>
              <li>
                <strong>Approve & Stake:</strong> Confirm both transactions in your wallet
              </li>
            </ol>

            <div className="info-box">
              <strong>💡 Tip:</strong> You can upgrade to a higher tier at any time, but you cannot downgrade. Upgrading resets your lock timer.
            </div>

            <h3>Claiming Rewards</h3>
            <p>
              Rewards accumulate automatically based on your weighted stake (amount × multiplier). Click "Claim Rewards" anytime to receive your pending $GHOST rewards.
            </p>

            <h3>Unstaking</h3>
            <p>
              You can only unstake after your lock period expires. Once unlocked:
            </p>
            <ol className="numbered-list">
              <li>Click "Unstake" in your staking position</li>
              <li>Enter the amount to withdraw</li>
              <li>Confirm the transaction</li>
            </ol>

            <div className="warning-box">
              <strong>⚠️ Voting Lock:</strong> If you vote on an active proposal, your tokens remain locked until that proposal fully resolves (voting period + execution delay + expiration window).
            </div>
          </section>

          {/* Proposals & Voting */}
          <section id="proposals" className="docs-section">
            <h2>📝 Proposals & Voting</h2>
            
            <h3>Creating a Proposal</h3>
            <p>
              Anyone with at least 1,000,000 $GHOST staked can create a governance proposal.
            </p>
            <ol className="numbered-list">
              <li>
                <strong>Stake Required Amount:</strong> You need 1M+ GHOST staked
              </li>
              <li>
                <strong>Click "Create Proposal":</strong> On the Voting page
              </li>
              <li>
                <strong>Enter Title:</strong> A clear, concise title for your proposal
              </li>
              <li>
                <strong>Enter Description:</strong> Detailed explanation of what you're proposing and why
              </li>
              <li>
                <strong>Submit:</strong> Confirm the transaction to create your proposal
              </li>
            </ol>

            <div className="info-box">
              <strong>💡 Best Practices:</strong> Before creating an on-chain proposal, discuss your idea with the community on Telegram or Twitter to gauge support.
            </div>

            <h3>Voting on Proposals</h3>
            <ol className="numbered-list">
              <li>
                <strong>Find Active Proposal:</strong> Proposals with "Active" status can be voted on
              </li>
              <li>
                <strong>Click "Vote":</strong> Opens the voting modal
              </li>
              <li>
                <strong>Choose For or Against:</strong> Your voting power equals your staked amount
              </li>
              <li>
                <strong>Confirm:</strong> Submit your vote transaction
              </li>
            </ol>

            <h3>Proposal States</h3>
            <table className="pools-table">
              <thead>
                <tr>
                  <th>State</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Pending</td>
                  <td>Proposal created, voting not yet started</td>
                </tr>
                <tr>
                  <td>Active</td>
                  <td>Voting is open (3 day window)</td>
                </tr>
                <tr>
                  <td>Defeated</td>
                  <td>Did not reach quorum or more Against votes</td>
                </tr>
                <tr>
                  <td>Timelocked</td>
                  <td>Passed, waiting for execution delay (2 days)</td>
                </tr>
                <tr>
                  <td>Awaiting Execution</td>
                  <td>Ready to be executed</td>
                </tr>
                <tr>
                  <td>Executed</td>
                  <td>Proposal has been executed</td>
                </tr>
                <tr>
                  <td>Expired</td>
                  <td>Was not executed within the window</td>
                </tr>
              </tbody>
            </table>

            <h3>Delegation</h3>
            <p>
              If you don't want to vote on every proposal yourself, you can delegate your voting power to another address:
            </p>
            <ol className="numbered-list">
              <li>Click "Delegate" in your staking position</li>
              <li>Enter the address you want to delegate to</li>
              <li>Confirm the transaction</li>
            </ol>
            <p>
              The delegate can vote on your behalf, but they cannot unstake or claim your rewards. You can remove delegation at any time.
            </p>

            <div className="warning-box">
              <strong>⚠️ Note:</strong> Delegating gives another address control over your votes. Only delegate to addresses you trust.
            </div>
          </section>

          {/* FAQ */}
          <section id="faq" className="docs-section">
            <h2>❓ Frequently Asked Questions</h2>
            
            <div className="faq-item">
              <h4>Is Ghost Protocol safe?</h4>
              <p>
                Ghost Protocol uses proven cryptographic techniques. However, it has not been professionally audited. Use at your own risk and never deposit more than you can afford to lose.
              </p>
            </div>

            <div className="faq-item">
              <h4>Can I withdraw to the same address I deposited from?</h4>
              <p>
                Yes, technically. But this defeats the purpose of privacy since observers can link the deposit and withdrawal. Always withdraw to a fresh address for maximum privacy.
              </p>
            </div>

            <div className="faq-item">
              <h4>How long should I wait before withdrawing?</h4>
              <p>
                The longer you wait, the more deposits join the pool, and the larger your anonymity set. There's no minimum, but waiting for more deposits increases privacy.
              </p>
            </div>

            <div className="faq-item">
              <h4>Can I make multiple deposits?</h4>
              <p>
                Yes! Each deposit generates a unique note. Keep track of all your notes separately.
              </p>
            </div>

            <div className="faq-item">
              <h4>What if I lose my note?</h4>
              <p>
                Your funds are permanently lost. There is no recovery mechanism. This is by design.
              </p>
            </div>

            <div className="faq-item">
              <h4>Can the developers access my funds?</h4>
              <p>
                No. The smart contracts are non-custodial. Only someone with your private note can withdraw your funds.
              </p>
            </div>

            <div className="faq-item">
              <h4>Why does withdrawal take so long?</h4>
              <p>
                The browser generates a zero-knowledge proof, which requires significant computation. This typically takes 30-60 seconds.
              </p>
            </div>

            <div className="faq-item">
              <h4>Can I withdraw to a different network?</h4>
              <p>
                No. Ghost Protocol operates only on Monad. Deposits and withdrawals must both be on Monad mainnet.
              </p>
            </div>

            <div className="faq-item">
              <h4>Do I lose my staked tokens when I vote?</h4>
              <p>
                No! Voting only temporarily extends your lock period until the proposal resolves. Your tokens remain in the staking contract and continue earning rewards. You never lose tokens from voting.
              </p>
            </div>

            <div className="faq-item">
              <h4>Can I unstake while a proposal is active?</h4>
              <p>
                Only if you haven't voted on it. Once you vote on a proposal, your tokens are locked until that proposal fully resolves (including the execution delay and expiration window).
              </p>
            </div>

            <div className="faq-item">
              <h4>How are staking rewards calculated?</h4>
              <p>
                Rewards are distributed proportionally based on your weighted stake (staked amount × tier multiplier). Higher lock tiers earn more rewards from the same pool.
              </p>
            </div>

            <div className="faq-item">
              <h4>Where do staking rewards come from?</h4>
              <p>
                Staking rewards are deposited into the reward pool by the protocol. The amount available for rewards depends on protocol revenue and community decisions.
              </p>
            </div>

            <div className="faq-item">
              <h4>What happens to the GHOST tokens when I mint an NFT?</h4>
              <p>
                The 100,000 GHOST tokens are sent to the burn address (0x...dEaD) and permanently destroyed. They cannot be recovered by anyone, ever. This reduces the total circulating supply.
              </p>
            </div>

            <div className="faq-item">
              <h4>Can I mint multiple Ghost PFP NFTs?</h4>
              <p>
                Yes! As long as you have enough GHOST tokens (100,000 per mint) and the collection hasn't sold out, you can mint as many as you want.
              </p>
            </div>

            <div className="faq-item">
              <h4>Are the Ghost PFPs stored on IPFS?</h4>
              <p>
                No. All Ghost PFP artwork is generated and stored entirely on-chain as SVG code. The images live directly in the smart contract and don't depend on any external storage.
              </p>
            </div>

            <div className="faq-item">
              <h4>Can I choose which traits my Ghost will have?</h4>
              <p>
                No. Traits are randomly generated at mint time using on-chain randomness. You won't know what you'll get until after minting - that's part of the fun!
              </p>
            </div>
          </section>

          {/* Legal */}
          <section id="legal" className="docs-section">
            <h2>⚖️ Legal Disclaimer</h2>
            
            <div className="legal-box">
              <h3>Important Legal Information</h3>
              
              <h4>No Warranty</h4>
              <p>
                Ghost Protocol is provided "as is" without warranty of any kind, express or implied. The developers make no representations or warranties regarding the security, reliability, or functionality of the protocol.
              </p>

              <h4>Use at Your Own Risk</h4>
              <p>
                By using Ghost Protocol, you acknowledge that:
              </p>
              <ul>
                <li>Smart contracts may contain bugs or vulnerabilities</li>
                <li>The protocol has not been professionally audited</li>
                <li>You may lose all deposited funds</li>
                <li>The developers cannot recover lost funds or notes</li>
              </ul>

              <h4>Regulatory Compliance</h4>
              <p>
                Privacy tools may be subject to regulations in your jurisdiction. It is YOUR responsibility to:
              </p>
              <ul>
                <li>Understand and comply with all applicable laws</li>
                <li>Report transactions as required by tax authorities</li>
                <li>Not use this protocol for illegal purposes</li>
                <li>Consult with legal professionals if uncertain</li>
              </ul>

              <h4>Prohibited Uses</h4>
              <p>
                Ghost Protocol must NOT be used for:
              </p>
              <ul>
                <li>Money laundering</li>
                <li>Terrorist financing</li>
                <li>Tax evasion</li>
                <li>Sanctions evasion</li>
                <li>Any other illegal activity</li>
              </ul>

              <h4>Jurisdiction</h4>
              <p>
                Privacy protocols have faced regulatory scrutiny in various jurisdictions. Users are responsible for understanding the legal status of such tools in their location. The protocol may not be available or legal in all jurisdictions.
              </p>

              <h4>Not Financial Advice</h4>
              <p>
                Nothing in this documentation constitutes financial, legal, or tax advice. Consult qualified professionals for guidance specific to your situation.
              </p>
            </div>

            <div className="danger-box" style={{ marginTop: '24px' }}>
              <strong>🚨 Final Warning:</strong> By using Ghost Protocol, you accept full responsibility for your actions and any consequences that may arise. The developers and contributors are not liable for any losses, legal issues, or other damages resulting from your use of this protocol.
            </div>
          </section>

          {/* Footer */}
          <footer className="docs-footer">
            <p>Ghost Protocol Documentation • Use at your own risk</p>
          </footer>
        </main>
      </div>
    </div>
  );
}

export default Docs;
